/**
 * TradingHub Component
 *
 * Main container for the complete trading workflow.
 * Receives draftId via URL param for refresh-safe session routing.
 * Handles phase navigation between pre-trading, live trading, and post-trading.
 *
 * Entry Flow:
 * - User selects session in Dashboard's SessionModal
 * - Navigates to /trading/:draftId
 * - TradingHub validates draftId and loads session
 * - Invalid/tombstoned draftId redirects to /dashboard
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sun, Moon, Monitor } from 'lucide-react';
// 2. Internal absolute imports
import { AppLayout, APP_FULLSCREEN_EVENT } from '@/components/shared/layouts';
import { AppSyncStatus } from '@/components/shared/sync';
import { SupportButton } from '@/featurebase';
import { useToastNotification, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from '@enfomentis/ui';
import { db } from '@/lib/db';
import { sessionDraftService } from '@/services/SessionDraftService';
import { Brand } from '@enfomentis/shared/config';
import { isTombstoned } from '@/utils/tombstone';
import { STATUS_TO_PHASE, PHASE_TO_STATUS } from '@/constants';
import { useLayoutConfig } from '@/hooks/useLayoutConfig';
import { useTheme, type AppTheme } from '@/hooks/useTheme';

// 3. Type imports
import type { SessionDraft, TimedPhaseStatus } from '@/types/transient/SessionDraft';
import type { TradingPhase, FlushHandler } from '@/types/shared';
import type { TradeDraft } from '@/types/transient/TradeDraft';

// 4. Relative imports
import { TradingLayout } from './TradingLayout';
import { ElapsedTimer } from './ElapsedTimer';
import { PreTradingPhase } from './PreTradingPhase';
import { LiveTradingPhase, LayoutDropdown, LayoutManagerControl, FeaturesDropdown } from './LiveTrading';
import { PostTradingPhase } from './PostTradingPhase';
import { SessionDropdown } from './SessionDropdown';
import { CarryForwardModal } from './CarryForwardModal';

/**
 * Main Trading Hub Component
 *
 * Features:
 * - URL param-based routing: receives draftId for refresh-safe sessions
 * - Full-screen AppLayout with hidden header/footer by default
 * - Validates draftId on mount, redirects to /dashboard if invalid
 * - Phase navigation between trading workflows
 */
export const TradingHub: React.FC = () => {
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId: string }>();
  const { showToast } = useToastNotification();
  const { currentTheme, setTheme } = useTheme();

  const getThemeIcon = (): React.ReactNode => {
    switch (currentTheme) {
      case 'light': return <Sun size={20} />;
      case 'dark': return <Moon size={20} />;
      case 'auto': return <Monitor size={20} />;
      default: return <Monitor size={20} />;
    }
  };

  // Loading state for draftId validation
  const [isValidating, setIsValidating] = useState(true);

  // Phase navigation
  const [currentPhase, setCurrentPhase] = useState<TradingPhase>('pre-trading');

  // Session state data (loaded from draft during validation)
  const [sessionType, setSessionType] = useState<SessionDraft['sessionType']>('live');
  const [sessionStatus, setSessionStatus] = useState<SessionDraft['status']>('pre-trading');

  // Phase timing state (loaded from draft, updated on stamp/transitions)
  const [phaseElapsedMs, setPhaseElapsedMs] = useState<SessionDraft['phaseElapsedMs']>();
  const [currentPhaseStartedAt, setCurrentPhaseStartedAt] = useState<string | undefined>();

  // T05 (WU2E): Trades accordion content for FeaturesDropdown
  const [tradesMenuContent, setTradesMenuContent] = useState<React.ReactNode>(null);

  // Carry-forward resolution modal state
  const [isCarryForwardOpen, setIsCarryForwardOpen] = useState(false);
  const [carryForwardDrafts, setCarryForwardDrafts] = useState<TradeDraft[]>([]);
  const [isCarryForwardCommitting, setIsCarryForwardCommitting] = useState(false);

  // Flush registration — active phase registers its flush handler via callback prop.
  // Default: resolved promise no-op (safe to call even if no phase has registered yet).
  const flushHandlerRef = useRef<FlushHandler>(async () => {});

  // Phase timing flush deduplication — prevents double-accumulation when
  // visibilitychange, pagehide, and unmount fire in rapid succession.
  // Draft-scoped: only reuses in-flight promise if draftId matches.
  const flushPromiseRef = useRef<Promise<boolean> | null>(null);
  const lastFlushDraftIdRef = useRef<string | null>(null);

  /** Deduped timing flush — returns in-flight promise if one exists for same draft */
  const flushTiming = useCallback((id: string): Promise<boolean> => {
    if (flushPromiseRef.current && lastFlushDraftIdRef.current === id) {
      return flushPromiseRef.current;
    }
    const p = sessionDraftService.flushPhaseElapsed(id);
    flushPromiseRef.current = p;
    lastFlushDraftIdRef.current = id;
    p.finally(() => {
      // Only clear refs if this is still the active flush — prevents an older
      // promise's cleanup from wiping refs owned by a newer in-flight flush.
      if (flushPromiseRef.current === p) {
        flushPromiseRef.current = null;
        lastFlushDraftIdRef.current = null;
      }
    });
    return p;
  }, []);

  const registerFlushHandler = useCallback((handler: FlushHandler): void => {
    flushHandlerRef.current = handler;
  }, []);

  // Layout config — loaded when live-trading phase is active
  const {
    layoutConfig,
    effectivePresetId,
    resolvedBannerConfig,
    savedLayouts,
    activeLayoutId,
    autosaveEnabled,
    isDirty,
    setActivePreset,
    handleWeightChange,
    handleSlotAssignmentChange,
    toggleBannerVisibility,
    setBannerPosition,
    toggleWidgetVisibility,
    updateClockConfig,
    setWidgetOrder,
    setWidgetAlignment,
    createSavedLayout,
    duplicateSavedLayoutById,
    renameSavedLayout,
    deleteSavedLayout,
    applySavedLayout,
    saveToActiveLayout,
    setAutosaveEnabled,
    flushPendingWeights,
  } = useLayoutConfig({ enabled: currentPhase === 'live-trading' });

  // Validate draftId on mount and set fullscreen mode
  useEffect(() => {
    let isMounted = true;

    // Set Trading Hub to full-screen mode by default
    localStorage.setItem(`${Brand.cachePrefix}-fullscreen`, JSON.stringify(true));
    // Dispatch event so AppLayout picks up the change (same-tab; storage events are cross-tab only)
    window.dispatchEvent(new CustomEvent(APP_FULLSCREEN_EVENT, { detail: { isFullScreen: true } }));

    const validateDraft = async (): Promise<void> => {
      // Missing draftId - redirect to dashboard
      if (!draftId) {
        navigate('/dashboard', { replace: true });
        return;
      }

      try {
        const draft = await db.session_drafts.get(draftId);

        if (!isMounted) return;

        // Invalid or tombstoned draft - redirect to dashboard
        if (!draft || isTombstoned(draft)) {
          console.warn('TradingHub: Invalid or deleted draft, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }

        // Valid draft - set initial phase and session state data
        const initialPhase = STATUS_TO_PHASE[draft.status] ?? 'pre-trading';
        setCurrentPhase(initialPhase);
        setSessionType(draft.sessionType);
        setSessionStatus(draft.status);
        setPhaseElapsedMs(draft.phaseElapsedMs);
        setCurrentPhaseStartedAt(draft.currentPhaseStartedAt);
        setIsValidating(false);

        // Stamp fresh currentPhaseStartedAt for the current phase.
        // Recovers stale timing if a previous flush failed.
        const stampOk = await sessionDraftService.stampPhaseStart(draftId);
        if (!isMounted) return;
        if (stampOk) {
          // Re-read draft for authoritative timing fields after stamp
          const stamped = await db.session_drafts.get(draftId);
          if (!isMounted) return;
          if (stamped && !isTombstoned(stamped)) {
            setPhaseElapsedMs(stamped.phaseElapsedMs);
            setCurrentPhaseStartedAt(stamped.currentPhaseStartedAt);
          }
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('TradingHub: Failed to validate draft:', error);
        navigate('/dashboard', { replace: true });
      }
    };

    validateDraft();

    return () => {
      isMounted = false;
    };
  }, [draftId, navigate]);

  /**
   * Execute the phase transition unconditionally (no carry-forward gate).
   * Called by handlePhaseChange (after gate check) and handleCarryForwardConfirm
   * (after decisions are persisted, unresolved drafts are cleared).
   */
  const executePhaseChange = useCallback(async (phase: TradingPhase): Promise<boolean> => {
    if (!draftId) {
      showToast({ color: 'danger', title: 'Session Error', description: 'No session ID found. Cannot update session status.' });
      return false;
    }

    // Await flush of pending autosave data before status transition
    try {
      await flushHandlerRef.current();
    } catch (error) {
      console.error('TradingHub: Flush failed before phase transition:', error);
      showToast({ color: 'danger', title: 'Save Failed', description: 'Failed to save pending data. Please try again.' });
      return false;
    }

    const newStatus = PHASE_TO_STATUS[phase];
    const success = await sessionDraftService.updateStatus(draftId, newStatus);

    if (!success) {
      showToast({ color: 'danger', title: 'Transition Failed', description: 'Failed to update session status. Please try again.' });
      return false;
    }

    // Re-read draft for authoritative timing fields, then set all state together
    // to avoid a transient frame pairing new status with old timing values
    try {
      const updated = await db.session_drafts.get(draftId);
      setCurrentPhase(phase);
      setSessionStatus(newStatus);
      if (updated && !isTombstoned(updated)) {
        setPhaseElapsedMs(updated.phaseElapsedMs);
        setCurrentPhaseStartedAt(updated.currentPhaseStartedAt);
      } else {
        // Draft missing or tombstoned — conservative fallback (same as catch)
        setPhaseElapsedMs(undefined);
        setCurrentPhaseStartedAt(new Date().toISOString());
      }
    } catch {
      // Non-critical — timer resets to 00:00:00 for the new phase; authoritative
      // timing restored on next successful draft re-read (transition or hub re-entry)
      setCurrentPhase(phase);
      setSessionStatus(newStatus);
      setPhaseElapsedMs(undefined);
      setCurrentPhaseStartedAt(new Date().toISOString());
    }

    // Reset flush handler — new phase will register its own on mount
    flushHandlerRef.current = async () => {};

    return true;
  }, [draftId, showToast]);

  /**
   * Gated phase change handler — intercepts live → post-trading to check for
   * unresolved drafts. Shows CarryForwardModal if any are found; otherwise
   * delegates directly to executePhaseChange.
   */
  const handlePhaseChange = useCallback(async (phase: TradingPhase): Promise<boolean> => {
    // Carry-forward gate: only applies to the live → post-trading transition
    if (phase === 'post-trading' && draftId) {
      // F02: flush pending data before reading DB so the gate sees the latest trade state
      try {
        await flushHandlerRef.current();
      } catch (error) {
        console.error('TradingHub: Flush failed before carry-forward gate:', error);
        showToast({ color: 'danger', title: 'Save Failed', description: 'Failed to save pending data. Please try again.' });
        return false;
      }
      try {
        const draft = await db.session_drafts.get(draftId);
        const unresolved = (draft?.trades ?? []).filter(
          (t) => t.carryForwardDecision === 'undecided' && !t.deletedAt
        );
        if (unresolved.length > 0) {
          setCarryForwardDrafts(unresolved);
          setIsCarryForwardOpen(true);
          return false;
        }
      } catch {
        // Check failed — proceed with transition (non-blocking gate)
      }
    }
    return executePhaseChange(phase);
  }, [draftId, showToast, executePhaseChange]);

  /**
   * Carry-forward confirm — persists per-draft decisions then executes the
   * live → post-trading transition via executePhaseChange (gate already cleared).
   */
  const handleCarryForwardConfirm = useCallback(async (
    decisions: Record<string, 'carry-forward' | 'do-not-carry'>
  ): Promise<void> => {
    if (!draftId) return;
    setIsCarryForwardCommitting(true);
    try {
      const draft = await db.session_drafts.get(draftId);
      // F04: surface null-draft failure instead of silently returning with modal open
      if (!draft) {
        showToast({ color: 'danger', title: 'Session Error', description: 'Session draft not found. Please try again.' });
        return;
      }
      const updatedTrades = (draft.trades ?? []).map((t) => {
        const decision = decisions[t.id];
        return decision !== undefined ? { ...t, carryForwardDecision: decision } : t;
      });
      // F03: check updateTrades return value — false means write failed without throwing
      const saved = await sessionDraftService.updateTrades(draftId, updatedTrades);
      if (!saved) {
        showToast({ color: 'danger', title: 'Save Failed', description: 'Failed to save carry-forward decisions. Please try again.' });
        return;
      }
      setIsCarryForwardOpen(false);
      setCarryForwardDrafts([]);
      // All decisions written — executePhaseChange will find no unresolved drafts
      await executePhaseChange('post-trading');
    } catch {
      // F04: surface thrown errors — modal stays open for retry
      showToast({ color: 'danger', title: 'Error', description: 'Failed to save carry-forward decisions. Please try again.' });
    } finally {
      setIsCarryForwardCommitting(false);
    }
  }, [draftId, showToast, executePhaseChange]);

  // Save & Exit — flush pending data + phase timing, then navigate to dashboard (no status change)
  const handleSaveAndExit = useCallback(async (): Promise<void> => {
    try {
      await flushHandlerRef.current();
    } catch (error) {
      console.error('TradingHub: Flush failed before Save & Exit:', error);
      showToast({ color: 'danger', title: 'Save Failed', description: 'Failed to save pending data. Please try again.' });
      return;
    }
    // Flush phase elapsed timing — awaited but non-blocking on failure
    if (draftId) {
      await flushTiming(draftId).catch((err) => {
        console.warn('TradingHub: Phase timing flush failed before Save & Exit:', err);
      });
    }
    navigate('/dashboard');
  }, [draftId, flushTiming, navigate, showToast]);

  // Best-effort phase timing flush on visibilitychange, pagehide, and unmount.
  // All calls go through flushTiming() (deduped via flushPromiseRef) to prevent
  // double-accumulation when multiple events fire in rapid succession.
  useEffect(() => {
    const id = draftId;
    if (!id) return;

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        void flushTiming(id).catch(() => {});
      }
    };

    const handlePageHide = (): void => {
      void flushTiming(id).catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      // Best-effort flush on unmount (in-app navigation away from TradingHub)
      void flushTiming(id).catch(() => {});
    };
  }, [draftId, flushTiming]);

  if (isValidating) {
    return (
      <AppLayout showBackButton headerContent={<AppSyncStatus />} hideFullscreenControls>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg text-text-secondary">Loading trading session...</div>
        </div>
      </AppLayout>
    );
  }

  const renderCurrentPhase = (): React.ReactNode => {
    switch (currentPhase) {
      case 'pre-trading':
        return <PreTradingPhase onPhaseChange={handlePhaseChange} draftId={draftId} registerFlushHandler={registerFlushHandler} />;
      case 'live-trading':
        return (
          <LiveTradingPhase
            registerFlushHandler={registerFlushHandler}
            layoutConfig={layoutConfig}
            effectivePresetId={effectivePresetId}
            resolvedBannerConfig={resolvedBannerConfig}
            onWeightChange={handleWeightChange}
            onSlotAssignmentChange={handleSlotAssignmentChange}
            flushPendingWeights={flushPendingWeights}
            {...(draftId !== undefined ? { sessionDraftId: draftId } : {})}
            onUpdateClockConfig={updateClockConfig}
            onTradesMenuChange={setTradesMenuContent}
          />
        );
      case 'post-trading':
        return <PostTradingPhase onPhaseChange={handlePhaseChange} draftId={draftId} registerFlushHandler={registerFlushHandler} />;
      default:
        return <PreTradingPhase onPhaseChange={handlePhaseChange} draftId={draftId} registerFlushHandler={registerFlushHandler} />;
    }
  };

  const sessionDropdown = (
    <SessionDropdown
      sessionStatus={sessionStatus}
      onPhaseChange={handlePhaseChange}
      onSaveAndExit={handleSaveAndExit}
    />
  );

  const themeToggle = (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button
          variant="light"
          size="sm"
          isIconOnly
          className="shrink-0"
          aria-label={`Theme: ${currentTheme}`}
        >
          {getThemeIcon()}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Theme selection"
        selectionMode="single"
        selectedKeys={[currentTheme]}
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as AppTheme;
          if (selected) setTheme(selected);
        }}
      >
        <DropdownItem key="light" startContent={<Sun size={16} />}>
          Light
        </DropdownItem>
        <DropdownItem key="dark" startContent={<Moon size={16} />}>
          Dark
        </DropdownItem>
        <DropdownItem key="auto" startContent={<Monitor size={16} />}>
          System
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );

  const actionMenuSlot = currentPhase === 'live-trading'
    ? (
      <>
        <SupportButton />
        <LayoutDropdown
          activePresetId={effectivePresetId}
          resolvedBannerConfig={resolvedBannerConfig}
          onPresetChange={setActivePreset}
          onBannerToggle={toggleBannerVisibility}
          onBannerPositionChange={setBannerPosition}
          onWidgetToggle={toggleWidgetVisibility}
          onWidgetOrderChange={setWidgetOrder}
          onWidgetAlignmentChange={setWidgetAlignment}
        />
        <LayoutManagerControl
          savedLayouts={savedLayouts}
          activeLayoutId={activeLayoutId}
          autosaveEnabled={autosaveEnabled}
          isDirty={isDirty}
          createSavedLayout={createSavedLayout}
          duplicateSavedLayoutById={duplicateSavedLayoutById}
          renameSavedLayout={renameSavedLayout}
          deleteSavedLayout={deleteSavedLayout}
          applySavedLayout={applySavedLayout}
          setAutosaveEnabled={setAutosaveEnabled}
          saveToActiveLayout={saveToActiveLayout}
        />
        <FeaturesDropdown
          {...(tradesMenuContent !== null ? { menuRegistry: { trades: tradesMenuContent } } : {})}
        />
        {themeToggle}
        <div className="hidden md:block w-px h-5 bg-divider mx-2" aria-hidden="true" />
        {sessionDropdown}
        <div className="hidden md:block w-px h-5 mx-0.5" aria-hidden="true" />
      </>
    )
    : (
      <>
        <SupportButton />
        {themeToggle}
        <div className="hidden md:block w-px h-5 bg-divider mx-2" aria-hidden="true" />
        {sessionDropdown}
        <div className="hidden md:block w-px h-5 mx-0.5" aria-hidden="true" />
      </>
    );

  // Compute current phase's accumulated ms for timer display
  const currentPhaseBaseMs = sessionStatus !== 'shelved'
    ? (phaseElapsedMs?.[sessionStatus as TimedPhaseStatus] ?? 0)
    : 0;

  return (
    <AppLayout showBackButton headerContent={<AppSyncStatus />} hideFullscreenControls>
      <CarryForwardModal
        isOpen={isCarryForwardOpen}
        onClose={() => setIsCarryForwardOpen(false)}
        onConfirm={handleCarryForwardConfirm}
        drafts={carryForwardDrafts}
        isCommitting={isCarryForwardCommitting}
      />
      <TradingLayout
        sessionType={sessionType}
        sessionStatus={sessionStatus}
        actionMenuSlot={actionMenuSlot}
        clockTimerSlot={
          <ElapsedTimer
            phaseElapsedMs={currentPhaseBaseMs}
            currentPhaseStartedAt={currentPhaseStartedAt}
          />
        }
        footer={null}
      >
        {renderCurrentPhase()}
      </TradingLayout>
    </AppLayout>
  );
};